import {
  Accordion,
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
  Search,
} from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertReports } from '@/entities/expert-report';
import { ReportSection } from './ReportSection';
import type { RuleWithPath } from './types';

type AddRulesDialogProps = {
  linkedRuleIds: number[];
  onAddRules: (rules: RuleWithPath[]) => void;
};

export const AddRulesDialog: FC<AddRulesDialogProps> = ({ linkedRuleIds, onAddRules }) => {
  const { t } = useTranslation(['common', 'coaching']);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRules, setSelectedRules] = useState<Map<number, RuleWithPath>>(new Map());
  const [expandedReports, setExpandedReports] = useState<string[]>([]);

  const { data: reports } = useExpertReports();

  const linkedSet = useMemo(() => new Set(linkedRuleIds), [linkedRuleIds]);

  const reportItems = useMemo(() => {
    if (!reports?.items) return [];
    return reports.items.filter((r) => !r.isDeleted);
  }, [reports?.items]);

  const handleToggleRule = useCallback((rule: RuleWithPath) => {
    setSelectedRules((prev) => {
      const next = new Map(prev);
      if (next.has(rule.id)) {
        next.delete(rule.id);
      } else {
        next.set(rule.id, rule);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    if (selectedRules.size === 0) return;
    onAddRules(Array.from(selectedRules.values()));
    setIsOpen(false);
    setSelectedRules(new Map());
    setSearch('');
    setExpandedReports([]);
  }, [selectedRules, onAddRules]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedRules(new Map());
      setSearch('');
      setExpandedReports([]);
    }
  }, []);

  const handleExpandedChange = useCallback((value: string[]) => {
    setExpandedReports(value);
  }, []);

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" icon={<PlusIcon />}>
          {t('coaching:linkExpertReportRules')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-160">
        <DialogHeader>
          <DialogTitle>{t('coaching:selectReportRules')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Search value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common:search')} />

          <ScrollArea className="max-h-96">
            {reportItems.length === 0 ? (
              <div className="text-muted-foreground p-4">{t('coaching:noReports')}</div>
            ) : (
              <Accordion type="multiple" value={expandedReports} onValueChange={handleExpandedChange}>
                {reportItems.map((report) => (
                  <ReportSection
                    key={report.id}
                    reportId={report.id}
                    reportName={report.name}
                    isExpanded={expandedReports.includes(String(report.id))}
                    search={search}
                    linkedSet={linkedSet}
                    selectedRules={selectedRules}
                    onToggleRule={handleToggleRule}
                  />
                ))}
              </Accordion>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleAdd} disabled={selectedRules.size === 0}>
            {t('common:add')} {selectedRules.size > 0 && `(${selectedRules.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
